import React from 'react';

function stripInlineBold(text) {
  return text.replace(/\*\*(.*?)\*\*/g, '$1');
}

/**
 * Renders structured AI text (markdown-lite) as readable React elements.
 * dark=true  → for dark-background chat bubbles (concierge)
 * dark=false → for light-background result cards (location match, local favorites)
 */
export default function FormattedMessage({ content, dark = true }) {
  if (!content) return null;

  const headingClass = dark
    ? 'font-semibold text-slate-100 mt-5 mb-2 text-[13px] uppercase tracking-wide border-b border-slate-600/50 pb-1'
    : 'font-semibold text-slate-800 mt-5 mb-2 text-[13px] uppercase tracking-wide border-b border-slate-200 pb-1';
  const paraClass = dark
    ? 'text-sm my-2.5 leading-relaxed'
    : 'text-sm my-2.5 leading-relaxed text-slate-700';
  const numberedClass = dark
    ? 'text-sm my-2 pl-4'
    : 'text-sm my-2 pl-4 text-slate-700';

  const lines = content.split('\n');
  const elements = [];
  let currentList = [];
  let inList = false;

  const flushList = (key) => {
    if (currentList.length === 0) return;
    elements.push(
      <ul key={key} className="my-3 space-y-2">
        {currentList.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <span className="text-signal-blue mt-1.5 text-xs flex-shrink-0">●</span>
            <span className={dark ? '' : 'text-slate-700'}>{stripInlineBold(item)}</span>
          </li>
        ))}
      </ul>
    );
    currentList = [];
    inList = false;
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (/^#{1,3}\s+/.test(trimmed)) {
      flushList(`list-h-${index}`);
      elements.push(
        <h4 key={`h-${index}`} className={headingClass}>
          {stripInlineBold(trimmed.replace(/^#{1,3}\s+/, ''))}
        </h4>
      );
      return;
    }

    if (/^\*\*[^*]+\*\*$/.test(trimmed)) {
      flushList(`list-bold-${index}`);
      elements.push(
        <h4 key={`h-${index}`} className={headingClass}>
          {trimmed.slice(2, -2)}
        </h4>
      );
      return;
    }

    if (/^[•\-]\s*/.test(trimmed) || /^\*\s+/.test(trimmed)) {
      if (!inList) {
        inList = true;
        currentList = [];
      }
      currentList.push(trimmed.replace(/^[•\-\*]\s*/, ''));
      return;
    }

    flushList(`list-${index}`);

    if (trimmed.endsWith(':') && trimmed.length < 60) {
      elements.push(
        <h4 key={`h-${index}`} className={headingClass}>
          {stripInlineBold(trimmed)}
        </h4>
      );
      return;
    }

    if (/^\d+[\.\)]\s/.test(trimmed)) {
      const match = trimmed.match(/^(\d+)[\.\)]\s+(.*)/);
      const num = match ? match[1] : '';
      const text = match ? match[2] : trimmed;
      elements.push(
        <div key={`num-${index}`} className="flex items-start gap-2.5 my-2">
          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-signal-blue flex items-center justify-center text-white text-[10px] font-bold mt-0.5">
            {num}
          </span>
          <span className={`text-sm font-semibold leading-relaxed ${dark ? 'text-slate-100' : 'text-slate-800'}`}>
            {stripInlineBold(text)}
          </span>
        </div>
      );
      return;
    }

    if (trimmed) {
      elements.push(
        <p key={`p-${index}`} className={paraClass}>
          {stripInlineBold(trimmed)}
        </p>
      );
      return;
    }

    if (index > 0 && index < lines.length - 1) {
      elements.push(<div key={`space-${index}`} className="h-3" />);
    }
  });

  flushList('list-final');

  return <div>{elements}</div>;
}
